import React from "react"
import {useParams, Link} from "react-router-dom"
import {useAccessToken} from "../../../services/auth"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import {MainSection} from "../../../containers/MainSection/MainSection"
import TitleBar from "../../../components/TitleBar/TitleBar"
import {
	DataTable,
	DataTableBody,
	DataTableCard,
	DataTableCell,
	DataTableHead,
	DataTableRow,
	TablePending,
} from "../../../components/DataTable/DataTable"
import {
	getAppConfigByOrgId,
	getWhiteLabelAppUsersByOrgId,
	AppConfig,
	WhiteLabelAppUser,
} from "../../../resources/whiteLabel"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import {getOrg, getOrgName} from "../../../resources/org"
import {JWTProviders} from "../../../resources/orgSettings"
import moment from "moment"
import Tag from "../../../components/Tag/Tag"
import {KeyValueCardView} from "../../../components/RenderKeyValueCard/KeyValueCardView"

import Icon from "../../../components/Icon/Icon"
import {useModal} from "react-modal-hook"
import ReadyToLaunchConfigModal from "./ReadyToLaunchConfigModal"

function AppConfigSection({config, onRefresh, orgId}: {config: AppConfig; onRefresh: () => void; orgId: string}) {
	const configDetails = {
		"JWT Issuer": config.attributes.jwtIssuer || "Not configured",
		"JWT Provider": (() => {
			const provider = config.attributes.jwtProvider?.provider
			if (!provider) return "Not configured"
			// Check if provider is a valid enum value and not Custom
			if (Object.values(JWTProviders).includes(provider as JWTProviders) && provider !== JWTProviders.Custom) {
				return provider
			}
			return "Custom"
		})(),
		"JWKS Path": (() => {
			const provider = config.attributes.jwtProvider?.provider
			if (provider === JWTProviders.Custom) {
				return "Not applicable for custom provider"
			}
			return config.attributes.jwtProvider?.jwksPath || "Not configured"
		})(),
		"JWT Public Key": config.attributes.jwtPublicKey ? "Configured" : "Not configured",
		"Application Form Version": config.attributes.applicationFormVersion,
		"End User Config URL": config.attributes.endUserConfigUrl || "Not configured",
		"Find End Users URL": config.attributes.findEndUsersUrl || "Not configured",
		"Banking Page URL": config.attributes.bankingPageURL || "Not configured",
		"Capital Page URL": config.attributes.capitalPageURL || "Not configured",
		"Billpay Page URL": config.attributes.billpayPageURL || "Not configured",
	}

	const [showEditConfigModal, hideEditConfigModal] = useModal(() => (
		<ReadyToLaunchConfigModal
			mode="edit"
			orgId={orgId}
			existingConfig={config}
			close={hideEditConfigModal}
			onSuccess={() => {
				onRefresh()
				hideEditConfigModal()
			}}
		/>
	))

	const editButton = (
		<button className="button is-small is-info" onClick={showEditConfigModal}>
			Edit
		</button>
	)

	return (
		<KeyValueCardView
			title="App Configuration"
			details={configDetails}
			singleColumn={true}
			additionalHeaderContent={editButton}
		/>
	)
}

function RTLUsersSection({users}: {users: WhiteLabelAppUser[]}) {
	return (
		<div className="rtl-users-section">
			<DataTableCard className="rtl-users-card">
				<TitleBar title={`Ready to Launch Users (${users.length})`} />
				<DataTable fullHeight={true} isEmpty={users.length === 0} noContentText={"No RTL users found"}>
					<DataTableHead>
						<tr>
							<th>External User ID</th>
							<th>User Role</th>
							<th>Customer ID</th>
							<th>Status</th>
							<th>Application Form ID</th>
							<th>Created At</th>
						</tr>
					</DataTableHead>
					<DataTableBody>
						{users.map((user) => {
							const customerId = user.relationships.customer?.data.id
							return (
								<DataTableRow key={user.id}>
									<DataTableCell>{user.attributes.externalUserId}</DataTableCell>
									<DataTableCell>
										<Tag
											size="small"
											variant="filled"
											theme={user.attributes.userRole === "Admin" ? "info" : "warning"}
											content={user.attributes.userRole}
										/>
									</DataTableCell>
									<DataTableCell>
										{customerId ? <Link to={`/customers/${customerId}`}>{customerId}</Link> : "—"}
									</DataTableCell>
									<DataTableCell>
										<Tag
											size="small"
											variant="filled"
											theme={user.attributes.status === "Active" ? "success" : "info"}
											content={user.attributes.status}
										/>
									</DataTableCell>
									<DataTableCell>{user.relationships.applicationForm?.data?.id || "—"}</DataTableCell>
									<DataTableCell>{moment(user.attributes.createdAt).format("L LT")}</DataTableCell>
								</DataTableRow>
							)
						})}
					</DataTableBody>
				</DataTable>
			</DataTableCard>
		</div>
	)
}

function NoConfigSection({orgId, onRefresh}: {orgId: string; onRefresh: () => void}) {
	const [showCreateConfigModal, hideCreateConfigModal] = useModal(() => (
		<ReadyToLaunchConfigModal
			mode="create"
			orgId={orgId}
			close={hideCreateConfigModal}
			onSuccess={() => {
				onRefresh()
				hideCreateConfigModal()
			}}
		/>
	))

	return (
		<div className="card">
			<div className="card-content has-text-centered">
				<div className="content">
					<h3>No Configuration Found</h3>
					<p>{"This org doesn't have an app configuration yet."}</p>
					<button className="button is-success" onClick={showCreateConfigModal}>
						<Icon icon="interface-add-1" size={12} />
						Create Configuration
					</button>
				</div>
			</div>
		</div>
	)
}

export default function ReadyToLaunchAdminOrg() {
	const {orgId} = useParams<{orgId: string}>()
	const accessToken = useAccessToken()

	const configResult = useAsyncResult(
		() => (orgId ? getAppConfigByOrgId(accessToken, orgId) : Promise.reject(new Error("No orgId"))),
		[orgId, accessToken]
	)
	const usersResult = useAsyncResult(
		() => (orgId ? getWhiteLabelAppUsersByOrgId(accessToken, orgId) : Promise.reject(new Error("No orgId"))),
		[orgId, accessToken]
	)
	const orgResult = useAsyncResult(
		() => (orgId ? getOrg(accessToken, orgId) : Promise.reject(new Error("No orgId"))),
		[orgId, accessToken]
	)

	const refreshData = () => {
		// Force refresh by re-calling the hooks with new dependency values
		window.location.reload()
	}

	const breadcrumbs = [
		{text: "Admin Dashboard", path: "/admin"},
		{text: "Ready to Launch", path: "/admin#tab=Ready+to+Launch"},
	]

	return (
		<>
			<AsyncResultComponent
				asyncResult={orgResult}
				pendingComponent={<TitleBar title={`Org ${orgId}`} breadcrumbs={breadcrumbs} />}
			>
				{({value: org}) => <TitleBar title={getOrgName(org)} breadcrumbs={breadcrumbs} />}
			</AsyncResultComponent>

			<MainSection>
				<div className="ready-to-launch-org-page">
					<div className="columns">
						<div className="column is-4">
							<AsyncResultComponent asyncResult={configResult} pendingComponent={<TablePending />}>
								{({value: config}) =>
									orgId ? (
										config ? (
											<AppConfigSection config={config} onRefresh={refreshData} orgId={orgId} />
										) : (
											<NoConfigSection orgId={orgId} onRefresh={refreshData} />
										)
									) : (
										<div className="notification is-warning">Org ID is required</div>
									)
								}
							</AsyncResultComponent>
						</div>

						<div className="column is-8">
							<AsyncResultComponent asyncResult={usersResult} pendingComponent={<TablePending />}>
								{({value: users}) => <RTLUsersSection users={users} />}
							</AsyncResultComponent>
						</div>
					</div>
				</div>
			</MainSection>
		</>
	)
}
